<!--
  - Copyright (C) 2020. Archimedes Exhibitions GmbH,
  - Saarbrücker Str. 24, Berlin, Germany
  -
  - This file contains proprietary source code and confidential
  - information. Its contents may not be disclosed or distributed to
  - third parties unless prior specific permission by Archimedes
  - Exhibitions GmbH, Berlin, Germany is obtained in writing. This applies
  - to copies made in any form and using any medium. It applies to
  - partial as well as complete copies.
  -->

<template>
  <div v-if="isSetup">
    <b-sidebar
      id="uploader"
      width="50vw"
      shadow
      v-if="
        allowEditing &&
        $cmsManager.bulkUploadContainers[$router.currentRoute.name]
      "
    >
      <uploader
        :parent-uuid="uuid"
        v-on:files-uploaded="onFilesUploaded"
      ></uploader>
    </b-sidebar>

    <delete-dialog ref="deleteDialog" v-on:delete="onDeleteNode">
    </delete-dialog>

    <batch-delete-dialog
      ref="batchDeleteDialog"
      v-on:delete="onDeleteBatchNodes"
    >
    </batch-delete-dialog>

    <!--  filter buttons, searchbar etc.  -->

    <b-row>
      <b-col>
        <b-dropdown
          v-if="allowEditing"
          class="mr-1"
          size="sm"
          variant="outline-primary"
          left
          :disabled="
            pickedNodes.length === 0 ||
            !userHasAccess([scopeMethods.WRITE, scopeMethods.DELETE], true)
          "
        >
          <template slot="button-content">
            {{ $t('cms.common.actions') }}
          </template>
          <b-dropdown-header>
            {{ $t('cms.common.batchHead') }}
          </b-dropdown-header>
          <b-dropdown-item
            class="my-2"
            variant="success"
            @click="onBatchPublish"
            :disabled="!userHasAccess([scopeMethods.WRITE], false)"
          >
            {{ $t('cms.list.publishSelected').toUpperCase() }}
          </b-dropdown-item>
          <b-dropdown-item
            class="my-2"
            variant="warning"
            @click="onBatchUnpublish"
            :disabled="!userHasAccess([scopeMethods.WRITE], false)"
          >
            {{ $t('cms.list.draftSelected').toUpperCase() }}
          </b-dropdown-item>
          <b-dropdown-item
            class="my-2"
            variant="danger"
            @click="onDeleteBatch"
            :disabled="!userHasAccess([scopeMethods.DELETE], false)"
          >
            <b-icon-trash class="mr-1"></b-icon-trash>
            {{ $t('cms.list.deleteSelected') }}
          </b-dropdown-item>
        </b-dropdown>

        <b-dropdown
          class="mr-1 d-none d-lg-inline"
          variant="dark"
          left
          size="sm"
        >
          <template slot="button-content">
            <b-icon-funnel></b-icon-funnel>
            <span class="d-none d-xl-inline">Filter</span>
          </template>
          <b-dropdown-header>
            {{ $t('cms.common.filterHead') }}
          </b-dropdown-header>
          <b-dropdown-form>
            <b-dropdown-group>
              <b-form-checkbox-group
                v-model="visibleTypes"
                @change="onThemeFilterChanged"
              >
                <b-form-checkbox
                  v-for="(type, i) in types"
                  v-bind:key="i"
                  :value="type"
                  class="mb-2 w-100 hover"
                >
                  {{ $cmsManager.getSchemaAlias(type) }}
                </b-form-checkbox>
              </b-form-checkbox-group>
            </b-dropdown-group>

            <b-dropdown-group>
              <b-form-checkbox-group
                v-model="visibleTags"
                @change="onTagFilterChanged"
              >
                <b-form-checkbox
                  v-for="(tag, i) in tags"
                  v-bind:key="i"
                  :value="tag"
                  class="mb-2 w-100 hover"
                >
                  {{ tag }}
                </b-form-checkbox>
              </b-form-checkbox-group>
            </b-dropdown-group>
          </b-dropdown-form>
        </b-dropdown>

        <b-dropdown
          class="mr-1 d-none d-xl-inline"
          variant="dark"
          left
          size="sm"
        >
          <template slot="button-content">
            <b-icon-layout-three-columns></b-icon-layout-three-columns>
          </template>
          <b-dropdown-header>
            {{ $t('cms.common.columnsHead') }}
          </b-dropdown-header>
          <b-dropdown-form>
            <b-form-checkbox-group
              v-model="fieldVisibility"
              @change="updateColumnStorage"
            >
              <b-form-checkbox
                v-for="(field, i) in pickableFields"
                class="mb-2 w-100 hover"
                v-bind:key="i"
                :value="field"
                :disabled="
                  fieldVisibility.length < 2 && fieldVisibility.includes(field)
                "
              >
                {{ $t('cms.common.' + field) }}
              </b-form-checkbox>
            </b-form-checkbox-group>
          </b-dropdown-form>
        </b-dropdown>

        <b-dropdown
          v-if="!perPage && totalCount > pageSizes[0]"
          class="mr-1 d-none d-lg-inline"
          variant="dark"
          left
          size="sm"
        >
          <template slot="button-content">
            <b-icon-three-dots-vertical></b-icon-three-dots-vertical>
          </template>
          <b-dropdown-header>
            {{ $t('cms.common.columnsHead') }}
          </b-dropdown-header>
          <b-dropdown-item
            class="mb-2"
            v-for="(size, i) in pageSizes"
            v-bind:key="i"
            @click="onPageSizeChanged(size)"
          >
            {{ size }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>

      <!--  search bar    -->

      <b-col cols="6">
        <div class="cms-search-wrapper">
          <input
            class="d"
            type="search"
            v-model="search"
            :placeholder="$t('cms.list.search')"
          />
        </div>
      </b-col>

      <!--  right buttons   -->

      <b-col>
        <div class="float-right" v-if="allowEditing && uuid">
          <b-button
            class="mr-1"
            variant="outline-primary d-none d-lg-inline"
            v-if="
              allowEditing &&
              $cmsManager.bulkUploadContainers[$router.currentRoute.name]
            "
            :disabled="!userHasAccess([scopeMethods.WRITE], false)"
            v-b-toggle.uploader
          >
            <b-icon-upload></b-icon-upload>
            Bulk upload
          </b-button>

          <b-button
            variant="primary"
            v-if="$cmsManager.getContainerSchemas(uuid).length === 1"
            :disabled="!userHasAccess([scopeMethods.WRITE], false)"
            @click="onCreateNode($cmsManager.getContainerSchemas(uuid)[0])"
          >
            <b-icon-plus></b-icon-plus>
            Add
          </b-button>

          <b-dropdown
            v-else
            :disabled="!userHasAccess([scopeMethods.WRITE], false)"
            variant="primary"
            right
          >
            <template v-slot:button-content>
              <b-icon-plus></b-icon-plus>
              Add
            </template>
            <b-dropdown-header
              >{{ $t('cms.common.typeSelect') }}
            </b-dropdown-header>
            <b-dropdown-item
              v-for="(schema, i) in $cmsManager
                .getContainerSchemas(uuid)
                .sort()"
              :key="i"
              @click="onCreateNode(schema)"
              class="my-2"
            >
              <b-icon
                v-if="$cmsManager.getSchemaIcon(schema)"
                :icon="$cmsManager.getSchemaIcon(schema)"
                class="mr-2"
              >
              </b-icon>
              {{ $cmsManager.getSchemaAlias(schema) }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-col>
    </b-row>

    <!--  main table  -->

    <b-table
      hover
      head-variant="light"
      class="row-pointer mt-5"
      ref="table"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :items="data"
      :fields="visibleFields"
      :sort-compare="sortTable"
      :busy="!showFirstBatch || isLoading"
      :no-sort-reset="true"
      primary-key="uuid"
      @row-clicked="onRowClicked"
      @sort-changed="onSortChanged"
    >
      <template v-slot:head()="scope">
        <div
          v-b-visible="
            (isVisible) => handleFieldVisibility(scope.field.key, isVisible)
          "
        >
          {{ scope.label }}
        </div>
      </template>

      <template v-slot:head(checked)>
        <b-form-checkbox v-model="allChecked" @change="updateAllChecked">
        </b-form-checkbox>
      </template>

      <template v-slot:cell(checked)="row">
        <b-form-checkbox
          v-model="row.item.checked"
          @change="(checked) => updateChecked(checked, row.item)"
        >
        </b-form-checkbox>
      </template>

      <template v-slot:cell(image)="row">
        <b-img
          v-if="row.item.thumbNail"
          rounded
          :src="row.item.thumbNail"
          :alt="getTruncatedString(row.item.displayName)"
          :height="thumbSize"
          :width="thumbSize"
          :id="`popover-${row.item.uuid}-${viewId}`"
        >
        </b-img>
        <b-popover
          v-if="row.item.thumbNail"
          :target="`popover-${row.item.uuid}-${viewId}`"
          triggers="hover"
          placement="right"
        >
          <b-img
            :src="row.item.preview"
            style="width: 100%"
            class="my-1"
            :alt="getTruncatedString(row.item.displayName)"
          >
          </b-img>
        </b-popover>
      </template>

      <template v-slot:cell(displayName)="row">
        <div>
          <span :id="`title-popover-${row.item.uuid}-${viewId}`">
            {{ getTruncatedString(row.item.displayName) }}
          </span>
          <b-popover
            placement="rightbottom"
            :target="`title-popover-${row.item.uuid}-${viewId}`"
            triggers="hover"
          >
            <div
              v-for="(field, i) in $cmsManager.getSchemaWithName(
                row.item.schema.name
              ).fields"
              :key="i"
            >
              <small
                v-if="field.type === 'string' && row.item.fields[field.name]"
              >
                <b>{{ $t('cms.common.' + field.name) }}:</b>
                {{ getTruncatedString(row.item.fields[field.name], 100) }}
              </small>
              <small
                v-else-if="field.type === 'date' && row.item.fields[field.name]"
              >
                <b>{{ $t('cms.common.' + field.name) }}:</b>
                {{ formatDate(row.item.fields[field.name], false) }}
              </small>
            </div>
          </b-popover>
        </div>
      </template>

      <template v-slot:cell(type)="row">
        <b-icon
          v-if="$cmsManager.getSchemaIcon(row.item.schema.name)"
          :icon="$cmsManager.getSchemaIcon(row.item.schema.name)"
        >
        </b-icon>
        <span
          :class="
            $cmsManager.getSchemaIcon(row.item.schema.name)
              ? 'd-none d-lg-inline'
              : null
          "
        >
          {{ $cmsManager.getSchemaAlias(row.item.schema.name) }}
        </span>
      </template>

      <template v-slot:cell(tags)="row">
        <b-badge
          variant="primary"
          class="mr-1"
          v-for="(tag, j) in row.item.tags.elements"
          :key="j"
        >
          {{ tag.name }}
        </b-badge>
      </template>

      <template v-slot:cell(status)="row">
        <div v-if="isChanged(row.item)" class="text-primary">
          <b-icon-circle-fill class="d-inline d-sm-none"></b-icon-circle-fill>
          <b variant="primary">{{ $t('cms.common.changed').toUpperCase() }}</b>
        </div>
        <div v-if="isPublished(row.item)" class="text-success">
          <b-icon-circle-fill class="d-inline d-sm-none"></b-icon-circle-fill>
          <b variant="success">{{
            $t('cms.common.published').toUpperCase()
          }}</b>
        </div>
        <div v-if="isDraft(row.item)" class="text-warning">
          <b-icon-circle-fill class="d-inline d-sm-none"></b-icon-circle-fill>
          <b variant="warning">{{ $t('cms.common.draft').toUpperCase() }}</b>
        </div>
      </template>

      <template v-slot:cell(languages)="row">
        <span
          class="text-uppercase"
          v-for="lang in row.item.availableLanguages"
          v-bind:key="lang"
        >
          {{ lang }}
        </span>
      </template>

      <template v-slot:cell(created)="row">
        <small>{{ formatDate(row.item.created) }}</small>
      </template>

      <template v-slot:cell(creator)="row">
        <span
          class="text-secondary"
          v-if="row.item.creator"
          :id="`creator-${row.item.creator.uuid}-${row.index}-${viewId}`"
        >
          {{ row.item.creator.username }}
        </span>
      </template>

      <template v-slot:cell(edited)="row">
        <small>{{ formatDate(row.item.edited) }}</small>
      </template>

      <template v-slot:cell(editor)="row">
        <span
          class="text-secondary"
          v-if="row.item.editor"
          :id="`editor-${row.item.editor.uuid}-${row.index}-${viewId}`"
        >
          {{ row.item.editor.username }}
        </span>
      </template>

      <template v-slot:head(actions)="scope">
        {{ scope.label }}
      </template>

      <template v-slot:cell(actions)="row">
        <b-button
          class="mb-1"
          size="sm"
          variant="dark"
          @click="onEditNode(row.item)"
        >
          <b-icon-pencil></b-icon-pencil>
        </b-button>
        <b-dropdown
          :disabled="!userHasAccess([scopeMethods.WRITE, scopeMethods.DELETE], true)"
          class="mb-1 ml-1"
          size="sm"
          variant="outline-dark"
          right
        >
          <template #button-content>
            <b-icon-three-dots-vertical></b-icon-three-dots-vertical>
          </template>
          <b-dropdown-item
            @click="onCopyNode(row.item)"
            :disabled="!userHasAccess([scopeMethods.WRITE], false)"
          >
            <b-icon-files></b-icon-files>
            Duplicate
          </b-dropdown-item>
          <b-dropdown-item
            @click="onDeleteDialog(row.item, true)"
            :disabled="!userHasAccess([scopeMethods.DELETE], false)"
          >
            <span class="text-danger"
              ><b-icon-trash></b-icon-trash> Delete</span
            >
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>

    <b-alert show v-if="!data.length && !isLoading" class="text-center">
      <b-icon-exclamation-diamond></b-icon-exclamation-diamond>
      {{ $t('ems.common.nothingFound') }}
    </b-alert>

    <b-pagination
      v-if="showFirstBatch && data.length"
      v-model="currentPage"
      :total-rows="totalCount"
      :per-page="pageSize"
      align="center"
      class="mt-5"
    >
    </b-pagination>

    <b-alert
      v-model="isLoading"
      class="position-fixed fixed-bottom m-0 rounded-0 text-center"
      style="z-index: 2000"
      variant="primary"
    >
      <b-icon-alarm></b-icon-alarm>
      {{ $t('cms.common.loadingWait') }}
    </b-alert>
  </div>
</template>

<script>
  // TODO: Take schema types from config
  import { utils as libUtils } from '@amdx/emslib'
  import utils from '../js/utils'

  export default {
    name: 'ListView',
    components: {
      Uploader: () => import('../components/Uploader'),
      DeleteDialog: () => import('../components/DeleteDialog'),
      BatchDeleteDialog: () => import('../components/BatchDeleteDialog')
    },

    props: {
      perPage: {
        default: null
      },
      version: {
        default: 'draft'
      },
      language: {
        default: null
      },
      allowEditing: {
        default: true
      },
      allowBatchPick: {
        default: true
      },
      schemasToLoad: {
        default: null
      },
      thumbSize: {
        default: 40
      },
      blackList: {
        default: null
      }
    },

    data () {
      return {
        isLoading: false,
        showFirstBatch: false,
        uuid: null,
        data: [],
        currentPage: 1,
        pageCount: 1,
        totalCount: null,
        pageSizes: [5, 10, 25, 50, 100],
        customPerPage: 10,
        search: '',
        searchPattern: /([\!\*\+\-\=\<\>\&\|\(\)\[\]\{\}\^\~\?\:\\/"])/g, // eslint-disable-line
        fieldVisibility: [],
        pickableFields: [],
        visibleTypes: [],
        visibleTags: [],
        allChecked: false,
        showUploader: false,
        viewId: null,
        sortBy: 'edited',
        sortDesc: true,
        tags: [],
        mySchemasToLoad: null,
        runningLoads: 0,
        isSetup: false
      }
    },

    computed: {
      visibleFields () {
        return this.fields.filter(
          (f) => !!this.fieldVisibility.find((k) => f.key === k)
        )
      },

      types: {
        get () {
          let types
          if (this.mySchemasToLoad) {
            types = this.mySchemasToLoad
          } else {
            let uuid
            if (this.uuid) {
              uuid = this.uuid
            } else {
              uuid = this.$cmsManager.getContainerUUID(
                this.$router.currentRoute.name
              )
            }
            types = this.$cmsManager.getContainerSchemas(uuid)
          }

          types.sort()
          return types
        }
      },

      fields () {
        let fields = [
          {
            key: 'checked',
            thClass: 'text-right',
            tdClass: 'asset-text-cell align-middle text-right'
          },
          {
            key: 'image',
            label: this.$t('cms.common.image'),
            sortable: false,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'displayName',
            label: this.$t('cms.common.title'),
            sortable: true,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'type',
            label: this.$t('cms.common.type'),
            sortable: false,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'tags',
            label: this.$t('cms.common.tags'),
            sortable: false,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'status',
            label: this.$t('cms.common.status'),
            sortable: false,
            thClass: 'asset-text-cell',
            tdClass: 'asset-text-cell align-middle'
          },
          {
            key: 'languages',
            label: this.$t('cms.common.languages'),
            sortable: false,
            thClass: 'd-none d-lg-table-cell',
            tdClass: 'd-none d-lg-table-cell align-middle'
          },
          {
            key: 'edited',
            label: this.$t('cms.common.edited'),
            sortable: true,
            sortDirection: 'desc',
            thClass: 'd-none d-xl-table-cell',
            tdClass: 'd-none d-xl-table-cell align-middle'
          },
          {
            key: 'editor',
            label: this.$t('cms.common.editor'),
            sortable: false,
            thClass: 'd-none d-xl-table-cell',
            tdClass: 'd-none d-xl-table-cell align-middle'
          },
          {
            key: 'actions',
            label: this.$t('cms.common.actions'),
            thClass: 'text-right',
            tdClass: 'asset-text-cell align-middle text-right'
          }
        ]

        if (this.schemasToLoad) {
          fields.splice(5, 1)
        }

        if (!this.allowBatchPick) {
          fields.splice(0, 1)
        }

        return fields
      },

      pageSize () {
        return this.perPage ? this.perPage : this.customPerPage
      },

      pickedNodes () {
        if (this.data) {
          return this.data.filter((n) => !!n.checked)
        }
        return []
      },

      scopeMethods () {
        return this.$cmsManager.scopeMethods
      }
    },

    watch: {
      $route: {
        immediate: true,
        handler () {
          if (!this.schemasToLoad) {
            this.uuid = this.$cmsManager.getContainerUUID(
              this.$router.currentRoute.name
            )
            if (this.uuid) {
              this.currentPage = 1
              this.loadData()
            }
          }
        }
      },

      schemasToLoad: {
        immediate: true,
        handler (schemas) {
          if (schemas !== this.mySchemasToLoad) {
            this.mySchemasToLoad = schemas
          }
        }
      },

      language () {
        this.loadData()
      },

      currentPage () {
        this.allChecked = false
        this.loadData()
      },

      search () {
        this.currentPage = 1
        this.allChecked = false
        this.loadData()
      }
    },

    methods: {
      async loadData () {
        this.runningLoads++
        let loadNumber = this.runningLoads

        this.isLoading = true
        this.showFirstBatch = false
        this.loadColumnState()
        this.loadTypeState()
        this.loadSortState()
        this.loadPageSizeState()
        this.allChecked = false
        this.showFirstBatch = true

        this.tags = []
        if (this.schemasToLoad) {
          let tagFamilies = this.$cmsManager.tagFamilies
          for (let tagFamily of tagFamilies) {
            let tags = await this.$cmsManager.getTags(tagFamily.uuid)
            if (loadNumber !== this.runningLoads) {
              return
            }
            for (let tag of tags.data) {
              if (!this.tags.includes(tag.name)) {
                this.tags.push(tag.name)
              }
            }
          }
        } else {
          this.uuid = this.$cmsManager.getContainerUUID(
            this.$router.currentRoute.name
          )
          let tagFamily = this.$cmsManager.getContainerTagFamily(this.uuid)
          let tags = await this.$cmsManager.getTags(tagFamily.uuid)
          if (loadNumber !== this.runningLoads) {
            return
          }
          for (let tag of tags.data) {
            this.tags.push(tag.name)
          }
        }
        this.tags.sort()
        this.loadTagState()

        let promise = null
        let search = this.search.replace(this.searchPattern, '\\$1')
        // TODO: Integrate pages, search
        if (this.mySchemasToLoad) {
          let types = this.visibleTypes.length
            ? this.visibleTypes
            : this.schemasToLoad
          promise = this.$cmsManager.getCompleteNodesOfSchemasSorted(
            // TODO: Muss auch in den anderen request
            types,
            this.visibleTags,
            search,
            this.getCurrentLanguage(),
            this.sortBy,
            this.sortDesc,
            this.version,
            this.currentPage,
            this.pageSize
          )
        } else {
          let types = this.visibleTypes.length ? this.visibleTypes : this.types
          promise = this.$cmsManager.getCompleteChildrenOfNodeSorted(
            this.uuid,
            this.getCurrentLanguage(),
            types,
            this.visibleTags,
            search,
            this.sortBy,
            this.sortDesc,
            this.version,
            this.currentPage,
            this.pageSize
          )
        }
        promise
          .then((data) => {
            if (loadNumber !== this.runningLoads) {
              return
            }
            this.currentPage = data.page
            this.pageCount = data.pageCount
            this.totalCount = data.totalCount
            if (this.schemasToLoad) {
              this.data = data.data.nodes.elements
            } else {
              this.data = data.data.node.children.elements
              if (data.data.node.uuid !== this.uuid) {
                this.isLoading = false
                this.runningLoads = 0
                return
              }
            }

            for (let node of this.data) {
              node.thumbNail = utils.getPreview(
                node,
                this.thumbSize,
                this.thumbSize
              )
              node.preview = utils.getPreview(node, 300, null, false)
            }

            this.isLoading = false
            this.runningLoads = 0
            return null
          })
          .catch((error) => {
            libUtils.toast(this, 'Loading data failed!', error)
            this.isLoading = false
            this.runningLoads = 0
            this.showFirstBatch = true
          })
      },

      getCurrentLanguage () {
        if (this.language) {
          return this.language
        }
        if (this.$router.currentRoute.query.lang) {
          return this.$router.currentRoute.query.lang
        }
        return this.$cmsManager.defaultLanguage
      },

      loadColumnState () {
        this.pickableFields = this.fields
          .map((f) => f.key)
          .filter((f) => f !== 'actions' && f !== 'checked')
        if (localStorage['column-' + this.$router.currentRoute.name]) {
          let fields = JSON.parse(
            localStorage['column-' + this.$router.currentRoute.name]
          )
          this.fieldVisibility = this.pickableFields.filter(
            (f) => fields[f] || fields[f] === undefined
          )
        } else {
          this.fieldVisibility = [...this.pickableFields]
        }
        this.fieldVisibility.unshift('checked')
        if (this.allowEditing) {
          this.fieldVisibility.push('actions')
        }
      },

      loadTypeState () {
        let storageKey = 'types-' + this.$router.currentRoute.name
        if (localStorage[storageKey]) {
          let types = JSON.parse(localStorage[storageKey])
          this.visibleTypes = this.types.filter(
            (t) => types[t] || types[t] === undefined
          )
        } else {
          this.visibleTypes = []
        }
      },

      loadTagState () {
        let storageKey = 'tags-' + this.$router.currentRoute.name
        if (localStorage[storageKey]) {
          let tags = JSON.parse(localStorage[storageKey])
          this.visibleTags = this.tags.filter(
            (t) => tags[t] || tags[t] === undefined
          )
        } else {
          this.visibleTags = []
        }
      },

      loadSortState () {
        let storageKey = 'sort-' + this.$router.currentRoute.name
        if (localStorage[storageKey]) {
          let sort = JSON.parse(localStorage[storageKey])
          this.sortBy = sort[0]
          this.sortDesc = sort[1]
        }
      },

      loadPageSizeState () {
        let storageKey = 'pageSize-' + this.$router.currentRoute.name
        if (localStorage[storageKey]) {
          this.customPerPage = localStorage[storageKey]
        }
      },

      onPageSizeChanged (size) {
        if (size === 'all') {
          this.customPerPage = Number.MAX_SAFE_INTEGER
        } else {
          this.customPerPage = size
        }
        this.updatePageSizeStorage()
        this.loadData()
      },

      onDeleteDialog (node, isExperiment = false) {
        // this.$refs.sidebar.close()
        this.$refs.deleteDialog.showQuestion(node.displayName, node.uuid)
      },

      onDeleteBatch () {
        this.$refs.batchDeleteDialog.showQuestion(this.pickedNodes)
      },

      onDeleteNode (uuid) {
        // this.$refs.sidebar.close()
        this.isLoading = true
        this.showFirstBatch = false
        let node = this.getNode(uuid)
        this.$cmsManager
          .getNodesLinkage([uuid])
          .then((data) => {
            if (data) {
              this.isLoading = false
              this.showFirstBatch = true
              this.$refs.deleteDialog.showLinkedWarning(
                node.displayName,
                data[uuid]
              )
            } else {
              this.$cmsManager
                .deleteNode(uuid)
                .then(() => {
                  this.loadData()
                })
                .catch((error) => {
                  this.isLoading = false
                  this.showFirstBatch = true
                  libUtils.toast(this, 'Delete content error', error)
                })
            }
          })
          .catch((error) => {
            this.isLoading = false
            this.showFirstBatch = true
            libUtils.toast(this, 'Delete content error', error)
          })
      },

      async onBatchPublish () {
        try {
          this.isLoading = true
          this.showFirstBatch = false
          for (let n of this.pickedNodes) {
            if (n.availableLanguages.includes(this.getCurrentLanguage())) {
              await this.$cmsManager.publishNode(n.uuid, n.language)
            }
          }
          this.loadData()
        } catch (error) {
          this.isLoading = false
          this.showFirstBatch = true
          libUtils.toast(this, 'Batch publish content error', error)
        }
      },

      onBatchUnpublish () {
        let nodes = this.pickedNodes.filter(
          (n) =>
            !!n.availableLanguages.includes(this.getCurrentLanguage()) &&
            (this.isChanged(n) || this.isPublished(n))
        )
        if (nodes.length === 0) {
          return
        }
        this.isLoading = true
        this.showFirstBatch = false
        this.$cmsManager
          .getNodesLinkage(
            nodes.map((n) => n.uuid),
            true
          )
          .then((data) => {
            if (data) {
              let linkedPairs = []
              for (let key in data) {
                linkedPairs.push([this.getNode(key), data[key]])
              }
              this.$refs.batchDeleteDialog.showLinkedWarning(
                linkedPairs,
                this.$t('cms.modals.notUnpublishable')
              )
              return nodes.filter((n) => !data[n.uuid])
            } else {
              return nodes
            }
          })
          .then(async (toBeDeleted) => {
            for (let n of toBeDeleted) {
              await this.$cmsManager.unpublishNode(n.uuid, n.language)
            }
            this.loadData()
          })
          .catch((error) => {
            this.isLoading = false
            this.showFirstBatch = true
            libUtils.toast(this, 'Batch delete content error', error)
          })
      },

      onDeleteBatchNodes (nodes) {
        this.isLoading = true
        this.showFirstBatch = false
        this.$cmsManager
          .getNodesLinkage(
            nodes.map((n) => n.uuid),
            true
          )
          .then((data) => {
            if (data) {
              let linkedPairs = []
              for (let key in data) {
                linkedPairs.push([this.getNode(key), data[key]])
              }
              this.$refs.batchDeleteDialog.showLinkedWarning(linkedPairs)
              return nodes.filter((n) => !data[n.uuid])
            } else {
              return nodes
            }
          })
          .then(async (toBeDeleted) => {
            for (let n of toBeDeleted) {
              await this.$cmsManager.deleteNode(n.uuid)
            }
            this.loadData()
          })
          .catch((error) => {
            this.isLoading = false
            this.showFirstBatch = true
            libUtils.toast(this, 'Batch delete content error', error)
          })
      },

      onCopyNode (node) {
        this.isLoading = true
        this.showFirstBatch = false
        let fields = Object.assign({}, node.fields)
        let schema = this.$cmsManager.getSchemaWithName(node.schema.name)
        fields[schema.displayField] = node.displayName + ' copy'
        let newNode = {
          language: node.language,
          schema: node.schema,
          parentNode: node.parentNode,
          fields: fields
        }
        this.$cmsManager
          .updateNode(newNode)
          .then(() => {
            this.loadData()
          })
          .catch((error) => {
            this.isLoading = false
            this.showFirstBatch = true
            libUtils.toast(this, 'Copy content error', error)
          })
      },

      onCreateNode (schemaName) {
        this.$router.push({
          name: this.$router.currentRoute.name + '-edit',
          params: { uuid: 'new' },
          query: {
            lang: this.getCurrentLanguage(),
            parentUuid: this.uuid,
            schemaName: schemaName
          }
        })
      },

      onEditNode (node) {
        this.$router.push({
          name: this.$router.currentRoute.name + '-edit',
          params: { uuid: node.uuid },
          query: {
            lang: this.getCurrentLanguage(),
            parentUuid: this.uuid,
            schemaName: node.schema.name
          }
        })
      },

      onFilesUploaded () {
        this.loadData()
      },

      onThemeFilterChanged (values) {
        this.currentPage = 1
        this.updateTypeStorage(values)
        this.loadData()
      },

      onTagFilterChanged (values) {
        this.currentPage = 1
        this.updateTagStorage(values)
        this.loadData()
      },

      onSortChanged (context) {
        this.currentPage = 1
        this.sortBy = context.sortBy
        this.sortDesc = context.sortDesc
        this.updateSortStorage()
        this.loadData()
      },

      updateSortStorage () {
        localStorage['sort-' + this.$router.currentRoute.name] = JSON.stringify(
          [this.sortBy, this.sortDesc]
        )
      },

      updatePageSizeStorage () {
        localStorage['pageSize-' + this.$router.currentRoute.name] =
          this.customPerPage
      },

      updateColumnStorage (values) {
        let data = {}
        for (let field of this.pickableFields) {
          data[field] = values.includes(field)
        }
        localStorage['column-' + this.$router.currentRoute.name] =
          JSON.stringify(data)
      },

      updateTypeStorage (values) {
        let data = {}
        for (let type of this.types) {
          data[type] = values.includes(type)
        }
        localStorage['types-' + this.$router.currentRoute.name] =
          JSON.stringify(data)
      },

      updateTagStorage (values) {
        let data = {}
        for (let tag of this.tags) {
          data[tag] = values.includes(tag)
        }
        localStorage['tags-' + this.$router.currentRoute.name] =
          JSON.stringify(data)
      },

      updateAllChecked (checked) {
        this.allChecked = checked
        for (let i in this.data) {
          this.$set(this.data[i], 'checked', checked)
        }
      },

      updateChecked (checked, node) {
        let i = this.data.indexOf(node)
        this.$set(this.data[i], 'checked', checked)
        this.checkIfAllChecked()
      },

      checkIfAllChecked () {
        for (let node of this.data) {
          if (!node.checked) {
            this.allChecked = false
            return
          }
        }
        this.allChecked = true
      },

      handleFieldVisibility (key, visibility) {
        let pickableFields = [...this.pickableFields]
        if (visibility && !pickableFields.includes(key)) {
          pickableFields.push(key)
        } else if (!visibility && pickableFields.includes(key)) {
          pickableFields = pickableFields.filter((k) => k !== key)
        } else {
          return
        }
        this.$set(this, 'pickableFields', pickableFields)
      },

      getTruncatedString (string, maxLength = 30) {
        return libUtils.getTruncatedString(string, maxLength)
      },

      sortTable (a, b, key) {
        if (key === 'edited') {
          return a.edited.localeCompare(b.edited)
        } else if (key === 'created') {
          return a.created.localeCompare(b.created)
        }
        return null
      },

      isChanged (node) {
        let langInfo = node.languages.find((d) => d.language === node.language)
        return utils.getPublishStatus(langInfo) === utils.publishStatus.CHANGED
      },

      isPublished (node) {
        let langInfo = node.languages.find((d) => d.language === node.language)
        return (
          utils.getPublishStatus(langInfo) === utils.publishStatus.PUBLISHED
        )
      },

      isDraft (node) {
        let langInfo = node.languages.find((d) => d.language === node.language)
        return utils.getPublishStatus(langInfo) === utils.publishStatus.DRAFT
      },

      formatDate (dateStr, includeTime = true) {
        if (dateStr) {
          return utils.formatDate(dateStr, includeTime)
        }
        return null
      },

      getNode (uuid) {
        return this.data.find((n) => n.uuid === uuid)
      },

      getPickedNodes () {
        return this.pickedNodes
      },

      onRowClicked (node, index) {
        console.debug(
          'row clicked: ',
          node,
          index,
          'allowEditing: ',
          this.allowEditing
        )
        if (this.allowEditing) {
          this.onEditNode(node)
        } else {
          this.$emit('row-clicked', node)
        }
      },

      userHasAccess (methods, isPermissive) {
        return this.$cmsManager.userHasAccess(methods, isPermissive)
      }
    },

    created () {
      this.viewId = utils.uuidv4()
      if (!this.$cmsManager.isSetup) {
        this.isLoading = true
        this.showFirstBatch = false
        this.$cmsManager
          .setup()
          .then(() => {
            this.isSetup = true
            this.loadData()
          })
          .catch((error) => {
            libUtils.toast(this, 'CMS setup failed', error)
            this.isLoading = false
            this.showFirstBatch = true
          })
      } else {
        this.isSetup = true
        this.loadData()
      }
    }
  }
</script>
